import React, { useCallback, useContext} from 'react';
import PropTypes from 'prop-types';
import { ModalNext, ModalFooter } from '@jutro/components';
import {WMICPolicyDetailsUtil} from 'gw-capability-policy-react';
import { useTranslator } from '@jutro/locale';
import { WMICButton } from 'wmic-components-platform-react';
import { DocumentService } from 'gw-capability-policydocument';
import styles from './WMICRcnModal.module.scss';
import messages from './WMICRcn.messages';
import { WMICDocumentDownloadButton } from 'gw-capability-document-react';
import { WMICTooltipCookieUtil, LobIconUtil, CONSTANTS, LOBConstants } from 'wmic-portals-utils-js';
import { useAuthentication } from 'wmic-digital-auth-react';
import { useCookies } from 'react-cookie';

import { Link } from '@jutro/router';

const WMICRcnModal = ({isOpen, onResolve, policyDetails, hideRcnCta, onClose}) => {
    const { authHeader, userInfo: authUserData } = useAuthentication();
    const [cookies, setCookie] = useCookies([]);

    const onSetCookies = useCallback(
        (params) => {
            setCookie(params);
        }, [setCookie]
    );

    const policyNumber = policyDetails.currentPeriod.policyNumber;
    // Only support PersonalAuto and PersonalProperty LOBs in AMP
    const lobType = !_.isEmpty(policyDetails.currentPeriod.lobs.personalAuto) ? LOBConstants.PA_LINE : LOBConstants.PP_LINE;
    let rcnDocument = WMICPolicyDetailsUtil.getDocumentByFormNumber(policyDetails.currentPeriod.documentDTOs, CONSTANTS.DOCUMENT_TYPE.RCN);
    
    if (!rcnDocument) {
        rcnDocument = WMICPolicyDetailsUtil.getDocumentByFormNumber(policyDetails.renewedPeriod?.documentDTOs, CONSTANTS.DOCUMENT_TYPE.RCN);
    }
    if (!rcnDocument) {
        rcnDocument = WMICPolicyDetailsUtil.getDocumentByFormNumber(policyDetails.currentPeriod.documentDTOs, CONSTANTS.DOCUMENT_TYPE.RCNH);
    }
    if (!rcnDocument) {
        rcnDocument = WMICPolicyDetailsUtil.getDocumentByFormNumber(policyDetails.renewedPeriod?.documentDTOs, CONSTANTS.DOCUMENT_TYPE.RCNH);
    }

    if (rcnDocument) {
        rcnDocument.id = rcnDocument.publicID;
        rcnDocument.friendlyName = "Price Increase Notice (PDF)";
        rcnDocument.type = "pc";
        rcnDocument.formNumber = rcnDocument.formNumber_WMIC;
    }

    const getIconClass = () => {
        const icon = LobIconUtil.getWmicIcon(lobType);
        return `fa ${icon} titleIconOrange`;
    };

    const handleDontShowAgain = useCallback(() => {
        onResolve();
        WMICTooltipCookieUtil.setDontShowAgainCookie(authUserData, onSetCookies, policyNumber);
        hideRcnCta(true);
        }, [authUserData, onSetCookies]
    );

    const getDecDocument = () => {
        const policyPeriod = policyDetails.renewedPeriod !== undefined ? policyDetails.renewedPeriod : policyDetails.currentPeriod;
        const decDocumentType = lobType === LOBConstants.PA_LINE ? CONSTANTS.DOCUMENT_TYPE.PADEC : CONSTANTS.DOCUMENT_TYPE.HODEC;
        const decDoc = WMICPolicyDetailsUtil.getDocumentByFormNumber(policyPeriod.documentDTOs, decDocumentType);

        return decDoc;
    };
    
    const decDocument = getDecDocument();
    const translator = useTranslator();
    const handleClose = () => {
        onResolve();
        localStorage.setItem('rcnContainerNotShown', JSON.stringify(false));
    };

    const handleDecDownload = () => {
        return DocumentService.downloadPolicyDocument(decDocument.publicID, decDocument.sessionID);
    }

    return (
        <ModalNext
            isOpen={isOpen}
            onRequestClose={handleClose}
            className={styles.modal}
            overlayClassName={styles.modalOverlay}
            shouldCloseOnOverlayClick={false}
            shouldCloseOnEsc={false}
            contentLayout={{
                component: 'Grid',
                componentProps: {
                    rows: ['auto'],
                },
            }}
        >
            
            <h3 className='modal'>
                <i className={`${styles.icon} ${getIconClass()}`}/>
                { lobType === LOBConstants.PA_LINE ? translator(messages.yourAutoInsuranceIncreased) : translator(messages.yourHomeownersInsuranceIncreased) }
            </h3>
            <div className={styles.modalBody}>
                {
                    <div className={styles.policy}>
                        <div>
                            <p className={styles.header}>
                                {translator(messages.reviewRenewal, { policyNumber: policyNumber })}
                            </p>
                            <p>
                                { lobType === LOBConstants.PA_LINE ? translator(messages.renewalMessagePA) : translator(messages.renewalMessagePP) }
                            </p>
                        </div>
                    </div>
                }
                <div>
                    <WMICDocumentDownloadButton
                        type='filled'
                        targetDocument={rcnDocument}
                    >
                        {translator(messages.priceIncreaseNotice)}
                    </WMICDocumentDownloadButton>
                </div>
                <div>
                    <Link
                        href={handleDecDownload()}
                        target='_blank'
                    >
                        {translator(messages.offerToRenew)}
                    </Link>
                </div>
            </div>
            <ModalFooter>
                <WMICButton
                    type='outlined'
                    onClick={handleDontShowAgain}
                >
                    {translator(messages.dontShowAgain)}
                </WMICButton>
                <WMICButton
                    type='primary'
                    onClick={handleClose}
                >
                    {translator(messages.close)}
                </WMICButton>
            </ModalFooter>
        </ModalNext>
    );
};

WMICRcnModal.propTypes = {
    policies: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string,
            details: PropTypes.object
        })
    ).isRequired,
};

export default WMICRcnModal;